/**
 * #648 — the focused card's visible claim, and its visible exit.
 *
 * #647 shipped this badge in three byte-identical copies (spread/total row,
 * dashboard moneyline row, GameDetail compact band) differing only in which
 * pills A/D aim at. #648 changes the copy on all three and adds the clear
 * control, so it lives in one place now.
 *
 * The ✕ is a REAL control, not a cue. On the moneyline surfaces a row/band
 * click toggles expansion and deliberately never touches focus (the ⌨ chip
 * is the one aim gesture), so without the ✕ the only mouse exit would be
 * Esc. The ✕ clears without touching expansion — the one gesture that can
 * never conflate the two.
 *
 * It carries `data-lt2-no-focus` so a focus-region click predicate ignores
 * it: on the spread/total rows the badge sits INSIDE the row's explicit
 * `data-lt2-focus-region`, and without the opt-out the clearing click would
 * immediately re-acquire the focus it just cleared.
 *
 * The capture harness (`scripts/capture-lt2-density.mjs`) finds this badge by
 * its EXACT trimmed text and fails the run when it is not visibly rendered —
 * keep `LT2_FOCUS_BADGE_TEXT` and that matcher in step.
 */
import { LT2_NO_FOCUS_ATTR } from '@/lib/trading/lt2FocusRegion';
import { hotkeyPairLabel } from '@/lib/trading/liveHotkeyRegistry';
import { useLiveHotkeyGrants, useLiveHotkeyMap } from '@/hooks/useLiveHotkeys';

/**
 * Exact rendered text AT THE SHIPPED DEFAULTS. Mirrored in
 * scripts/capture-lt2-density.mjs, which measures the default mapping, and
 * pinned equal to `lt2FocusBadgeText({})` by
 * `src/components/liveHotkeyTeaching.test.tsx` — so a
 * change to either the defaults or the format fails rather than silently
 * desyncing the harness.
 *
 * #725: the RENDERED text follows the trader's own keys. The width note below
 * is why it stays this terse — a longer pair widens the moneyline board's
 * intrinsic width, and the density budgets are measured at the defaults.
 */
export const LT2_FOCUS_BADGE_TEXT = '⌨ A/D ✕';

/** The badge's text for a given mapping. `⌨ <post-top>/<post-bottom> ✕`. */
export function lt2FocusBadgeText(
  map: Parameters<typeof hotkeyPairLabel>[0],
  grants: Parameters<typeof hotkeyPairLabel>[3],
): string {
  return `⌨ ${hotkeyPairLabel(map, 'passive-top', 'passive-bottom', grants)} ✕`;
}

interface Lt2FocusBadgeProps {
  /** What A and D post on THIS surface, e.g. "A posts NO on the top team, D posts NO on the bottom team". */
  sides: string;
  /** Surface-specific caveat, e.g. "Collapse the row to fire." Omitted when none applies. */
  caveat?: string;
  /** Release this card's focus. Equality-guarded by the caller. */
  onClear: () => void;
}

/**
 * The pre-focus half of the same control: the visible, explicit way to aim
 * A/D at a GAME without expanding anything. Rendered once per hotkey-eligible
 * moneyline game (on its top team's row/band) while one-click is ARMED and
 * the game is not focused — before this existed the only mouse gesture that
 * acquired focus also toggled expansion, so the armed trader's first A/D
 * press was rejected and nothing on screen taught the initial gesture.
 * Carries `data-lt2-hotkey-control` and blurs itself so its own residual DOM
 * focus can never own the very next keypress.
 */
export function Lt2FocusGhostChip({ onFocus }: { onFocus: () => void }) {
  const map = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const pair = hotkeyPairLabel(map, 'passive-top', 'passive-bottom', grants);
  const [postTop, postBottom] = pair.split('/');
  return (
    <button
      type="button"
      data-lt2-hotkey-control=""
      {...{ [LT2_NO_FOCUS_ATTR]: '' }}
      onClick={(e) => {
        // The row beneath expands on click — aiming the hotkeys must not.
        e.stopPropagation();
        e.currentTarget.blur();
        onFocus();
      }}
      // Glyph-only on purpose: the chip repeats per eligible game, and its
      // earlier "⌨ A/D" text widened the moneyline board's intrinsic width
      // past the 1470/1920 density budgets (capture-harness finding). The
      // wordy teach lives in ONE place now — the header target readout's
      // "A/D — aim a game" — and this chip's aria-label/title carry the same
      // meaning for hover and assistive tech.
      className="border-border/60 bg-muted/40 text-muted-foreground hover:border-primary/60 hover:text-primary shrink-0 rounded border px-1 py-px text-[10px] font-semibold"
      aria-label={`Aim ${pair} hotkeys at this game`}
      title={`Aim ${postTop} and ${postBottom} at this game: ${postTop} posts NO on the top team's market, ${postBottom} posts NO on the bottom team's market (does not expand anything). Esc clears.`}
    >
      ⌨
    </button>
  );
}

export function Lt2FocusBadge({ sides, caveat, onClear }: Lt2FocusBadgeProps) {
  const map = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  return (
    <button
      type="button"
      {...{ [LT2_NO_FOCUS_ATTR]: '' }}
      onClick={(e) => {
        // The row beneath expands on click and the ladder rows trade — a
        // clear must do neither.
        e.stopPropagation();
        onClear();
      }}
      className="shrink-0 rounded bg-primary/25 px-1 py-px text-[10px] font-semibold text-primary hover:bg-primary/40"
      aria-label="Clear hotkey focus"
      title={`Hotkey focus — ${sides}, at the visible one-click size.${
        caveat ? ` ${caveat}` : ''
      } Click ✕ or press Esc to clear.`}
    >
      {lt2FocusBadgeText(map, grants)}
    </button>
  );
}
